import React, { useEffect, useState, useMemo } from "react";
import { Constants } from "@eagerdog/constants";
import {
  ScentworkScoreDto,
  ScentworkFaultDto,
  IShow,
  IRunningOrderDogClass,
} from "@eagerdog/interfaces";

import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import StopWatch from "../StopWatch/StopWatch";
import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";

import styles from "./ScentworkResults.module.scss";

interface IProps {
  result: any,
  show?: IShow,
  // Other scores in the same show (used to default hides/time limit from an
  // already-entered dog in this class when the judge schedule has no values)
  showScores?: any[],

  onChange(result: ScentworkScoreDto[]): void
}

const ScentworkResults: React.FC<IProps> = (props) => {
  const nqReasons: IOption[] = [
    {
      id: Constants.scentwork_nq_reason.incorrect_alert_finish_call,
      value: Constants.scentwork_nq_reason.incorrect_alert_finish_call
    },
    { id: Constants.scentwork_nq_reason.harsh_correction, value: Constants.scentwork_nq_reason.harsh_correction },
    {
      id: Constants.scentwork_nq_reason.significant_disruption_of_area,
      value: Constants.scentwork_nq_reason.significant_disruption_of_area
    },
    {
      id: Constants.scentwork_nq_reason.handler_unable_to_point_to_hide,
      value: Constants.scentwork_nq_reason.handler_unable_to_point_to_hide
    },
    { id: Constants.scentwork_nq_reason.max_time_exceeded, value: Constants.scentwork_nq_reason.max_time_exceeded }
  ];

  const excusedReasons: IOption[] = [
    {
      id: Constants.scentwork_excused_reason.dog_eliminated_in_search_area,
      value: Constants.scentwork_excused_reason.dog_eliminated_in_search_area
    },
    {
      id: Constants.scentwork_excused_reason.dog_overly_stressed,
      value: Constants.scentwork_excused_reason.dog_overly_stressed
    },
    {
      id: Constants.scentwork_excused_reason.handler_request,
      value: Constants.scentwork_excused_reason.handler_request
    },
    { id: Constants.scentwork_excused_reason.other, value: Constants.scentwork_excused_reason.other }
  ];

  const resultOptions: IOption[] = [
    { id: "None", value: "None" },
    { id: Constants.score_result_type.qualified, value: "Qualified" },
    { id: Constants.score_result_type.not_qualified, value: "Not Qualified" },
    { id: Constants.score_result_type.disqualified, value: "Disqualified" },
    { id: Constants.score_result_type.excused, value: "Excused" },
    { id: Constants.score_result_type.absent, value: "Absent" }
  ]

  const { activeTab, setTab } = useTabContent();
  
  // Determine if this level needs multiple searches
  const isMaster = props.result.dog_class.level === Constants.dog_class_element_level.master || 
                   props.result.dog_class.level === Constants.dog_class_element_level.scentwork_master;
  const isExcellent = props.result.dog_class.level === Constants.dog_class_element_level.excellent ||
                      props.result.dog_class.level === Constants.dog_class_element_level.scentwork_excellent;
  const isInterior = props.result.dog_class.show_element === Constants.dog_class_element.scentwork_interior;
  
  // Determine number of searches needed
  const numSearches = useMemo(() => {
    if (isMaster && isInterior) return 3; // Master Interior = 3 searches
    if (isMaster && !isInterior) return 1; // Master Exterior/Buried/Container = 1 search
    if (isExcellent && isInterior) return 2; // Excellent Interior = 2 searches
    return 1; // Everything else = 1 search
  }, [isMaster, isExcellent, isInterior]);
  
  // Dynamically create tabs based on number of searches needed
  const tabs = useMemo<ITab[]>(() => {
    return Array.from({ length: numSearches }, (_, i) => ({
      name: `Search ${i + 1}`,
      actions: []
    }));
  }, [numSearches]);

  const getOptionLabel = (id: string) => {
    switch (id) {
      case Constants.score_result_type.qualified:
        return "Qualified";
      case Constants.score_result_type.not_qualified:
        return "Not Qualified";
      case Constants.score_result_type.disqualified:
        return "Disqualified";
      case Constants.score_result_type.excused:
        return "Excused";
      case Constants.score_result_type.absent:
        return "Absent";
    }

    return "None";
  }

  const getFault = (faultType: string, index: number) => {
    const faults = isMaster ? props.result.scentwork_master?.[index]?.faults : props.result.scentwork?.faults;

    if (faults) {
      let hasFault = faults.filter((f: ScentworkFaultDto) => {
        return f.fault_type === faultType;
      });

      if (hasFault.length > 0) {
        return hasFault[0].fault_count.toString();
      }
    }

    return "0";
  }

  const findRunningOrderDogClass = (): IRunningOrderDogClass | null => {
    const rings = props.show?.rings;
    const showElement = props.result.dog_class?.show_element;
    const level = props.result.dog_class?.level;

    if (!rings || !rings.length) {
      return null;
    }

    for (const ring of rings) {
      for (const runningOrder of ring.running_order ?? []) {
        const matchingClass = runningOrder.dog_classes.find((dogClass) =>
          dogClass.show_element === showElement &&
          dogClass.level === level
        );

        if (matchingClass) {
          return matchingClass;
        }
      }
    }

    return null;
  }

  const runningOrderDogClass = findRunningOrderDogClass();

  // Defaults come from the judge-schedule tile (per-search field, then the
  // plain field). When the tile has nothing - no tile for this class, or the
  // secretary never set values - fall back to the most recently saved score
  // in the same class so the "first dog entered sets the defaults" behaviour
  // doesn't depend on the schedule being in sync.
  const classSiblingScores = useMemo(() => {
    const showElement = props.result.dog_class?.show_element;
    const level = props.result.dog_class?.level;
    const ownId = props.result._id?.toString();

    return (props.showScores || [])
      .filter((s) =>
        s._id?.toString() !== ownId &&
        s.dog_class?.show_element === showElement &&
        s.dog_class?.level === level
      )
      .sort((a, b) => {
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
        return bTime - aTime;
      });
  }, [props.showScores, props.result]);

  const usesMasterArray = isMaster || (isExcellent && isInterior);

  const getTileDefault = (index: number, field: "time_limit" | "num_hides") => {
    const perSearch = (runningOrderDogClass as any)?.[`${field}_search_${index + 1}`];
    return perSearch || (runningOrderDogClass as any)?.[field] || undefined;
  };

  const getSiblingDefault = (index: number, field: "time_limit" | "num_hides") => {
    for (const sibling of classSiblingScores) {
      const value = usesMasterArray
        ? sibling.scentwork_master?.[index]?.[field]
        : sibling.scentwork?.[field];
      if (value) {
        return value;
      }
    }
    return undefined;
  };

  const defaultTimeLimit = (index: number): string =>
    getTileDefault(index, "time_limit") || getSiblingDefault(index, "time_limit") || "";

  const defaultNumHides = (index: number): number =>
    Number(getTileDefault(index, "num_hides") || getSiblingDefault(index, "num_hides") || 0);

  const requiresNqReason = useMemo(() => {
    return [
      Constants.score_result_type.not_qualified,
      Constants.score_result_type.disqualified
    ]
  }, []);

  const requiresExcusedReason = useMemo(() => {
    return [
      Constants.score_result_type.excused
    ]
  }, []);

  const createDefaultResult = (index: number) => ({
    result: { id: Constants.score_result_type.qualified, value: "Qualified" },
    searchTime: "",
    timeLimit: defaultTimeLimit(index),
    numHides: defaultNumHides(index),
    numFinds: 0,
    handlerError: getFault(Constants.scentwork_fault_type.handler_error, index),
    safetyConcern: getFault(Constants.scentwork_fault_type.safety_concern, index),
    mdoa: getFault(Constants.scentwork_fault_type.mild_disruption_of_area, index),
    nqReason: {
      id: Constants.scentwork_nq_reason.incorrect_alert_finish_call,
      value: Constants.scentwork_nq_reason.incorrect_alert_finish_call
    },
    excusedReason: {
      id: Constants.scentwork_excused_reason.dog_eliminated_in_search_area,
      value: Constants.scentwork_excused_reason.dog_eliminated_in_search_area
    },
    otherReason: ""
  });

  const [results, setResults] = useState<Array<{
    result: IOption;
    searchTime: string;
    timeLimit: string;
    numHides: number;
    numFinds: number;
    handlerError: string;
    safetyConcern: string;
    mdoa: string;
    nqReason: IOption;
    excusedReason: IOption;
    otherReason: string;
  }>>(() => {
    // Use master array for Master level or Excellent Interior
    if (isMaster || (isExcellent && isInterior)) {
      const masterResults = props.result.scentwork_master ?? [];

      return Array.from({ length: numSearches }, (_, index) => {
        if (masterResults[index]) {
          const item = masterResults[index];
          return {
            result: {
              id: item?.result_type || Constants.score_result_type.qualified,
              value: item?.result_type ? getOptionLabel(item.result_type) : "Qualified"
            },
            searchTime: item?.search_time ? item.search_time.toString() : "",
            timeLimit: item?.time_limit || defaultTimeLimit(index),
            numHides: item?.num_hides || defaultNumHides(index),
            numFinds: item?.num_finds || 0,
            handlerError: getFault(Constants.scentwork_fault_type.handler_error, index),
            safetyConcern: getFault(Constants.scentwork_fault_type.safety_concern, index),
            mdoa: getFault(Constants.scentwork_fault_type.mild_disruption_of_area, index),
            nqReason: item?.result_reason
              ? { id: item.result_reason, value: item.result_reason }
              : { id: Constants.scentwork_nq_reason.incorrect_alert_finish_call, value: Constants.scentwork_nq_reason.incorrect_alert_finish_call },
            excusedReason: item?.result_reason
              ? { id: item.result_reason, value: item.result_reason }
              : { id: Constants.scentwork_excused_reason.dog_eliminated_in_search_area, value: Constants.scentwork_excused_reason.dog_eliminated_in_search_area },
            otherReason: item?.result_other_text || ""
          };
        }
        return createDefaultResult(index);
      });
    }

    const _result = props.result.scentwork;
    return [{
      result: {
        id: _result?.result_type ? _result?.result_type : Constants.score_result_type.qualified,
        value: _result?.result_type ? getOptionLabel(_result?.result_type) : "Qualified"
      },
      searchTime: _result?.search_time ? _result?.search_time.toString() : "",
      timeLimit: !!_result?.time_limit ? _result?.time_limit : defaultTimeLimit(0),
      numHides: !!_result?.num_hides ? _result?.num_hides : defaultNumHides(0),
      numFinds: _result?.num_finds ?? 0,
      handlerError: getFault(Constants.scentwork_fault_type.handler_error, 0),
      safetyConcern: getFault(Constants.scentwork_fault_type.safety_concern, 0),
      mdoa: getFault(Constants.scentwork_fault_type.mild_disruption_of_area, 0),
      nqReason: _result?.result_reason
        ? { id: _result.result_reason || "", value: _result.result_reason || "" }
        : {
          id: Constants.scentwork_nq_reason.incorrect_alert_finish_call,
          value: Constants.scentwork_nq_reason.incorrect_alert_finish_call
        },
      excusedReason: _result?.result_reason
        ? { id: _result.result_reason || "", value: _result.result_reason || "" }
        : {
          id: Constants.scentwork_excused_reason.dog_eliminated_in_search_area,
          value: Constants.scentwork_excused_reason.dog_eliminated_in_search_area
        },
      otherReason: _result?.result_other_text ? _result?.result_other_text : ""
    }];
  });

  const getResultValue = (field: string) => {
    // Use activeTab for multiple searches (Master all elements, Excellent Interior)
    if (numSearches > 1) {
      // @ts-expect-error
      return results[activeTab][field];
    }

    // @ts-expect-error
    return results[0][field];
  }

  const updateResult = (field: string, value: any) => {
    // Use activeTab for multiple searches (Master all elements, Excellent Interior)
    const index = numSearches > 1 ? activeTab : 0;

    setResults(prev => prev.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  useEffect(() => {
    const payload = results.map(resultItem => {
      const resultPayload: ScentworkScoreDto = {
        time_limit: resultItem.timeLimit,
        num_hides: resultItem.numHides,
        num_finds: resultItem.numFinds,
        faults: [
          { fault_type: Constants.scentwork_fault_type.handler_error, fault_count: Number(resultItem.handlerError) },
          { fault_type: Constants.scentwork_fault_type.safety_concern, fault_count: Number(resultItem.safetyConcern) },
          { fault_type: Constants.scentwork_fault_type.mild_disruption_of_area, fault_count: Number(resultItem.mdoa) }
        ],
        result_type: resultItem.result.id,
        search_time: resultItem.searchTime,
      };

      if (requiresNqReason.includes(resultItem.result.id)) {
        resultPayload.result_reason = resultItem.nqReason.id;
      }

      if (requiresExcusedReason.includes(resultItem.result.id) && resultItem.excusedReason.id !== Constants.scentwork_excused_reason.other) {
        resultPayload.result_reason = resultItem.excusedReason.id;
      }

      if (requiresExcusedReason.includes(resultItem.result.id) && resultItem.excusedReason.id === Constants.scentwork_excused_reason.other) {
        resultPayload.result_reason = resultItem.excusedReason.id;
        resultPayload.result_other_text = resultItem.otherReason;
      }

      return resultPayload;
    });

    props.onChange(payload);
  }, [results]);

  const renderResultForm = () => {
    return (
      <div className={styles.ScentworkResults}>
        {(!!runningOrderDogClass?.num_hides || !!runningOrderDogClass?.time_limit || 
          !!runningOrderDogClass?.num_hides_search_1 || !!runningOrderDogClass?.time_limit_search_1 ||
          !!runningOrderDogClass?.num_hides_search_2 || !!runningOrderDogClass?.time_limit_search_2 ||
          !!runningOrderDogClass?.num_hides_search_3 || !!runningOrderDogClass?.time_limit_search_3) ? (
          <div className={styles.elementConfig}>
            <div className={styles.configTitle}>Element Configuration</div>
            <div className={styles.configDetails}>
              {/* Non-Master/Non-Excellent-Interior level */}
              {!!runningOrderDogClass?.num_hides && !runningOrderDogClass?.num_hides_search_1 && (
                <span><strong>Number of Hides:</strong> {runningOrderDogClass.num_hides}</span>
              )}
              {!!runningOrderDogClass?.time_limit && !runningOrderDogClass?.time_limit_search_1 && (
                <span><strong>Time Limit:</strong> {runningOrderDogClass.time_limit}</span>
              )}
              {/* Search 1 */}
              {!!runningOrderDogClass?.num_hides_search_1 && (
                <span><strong>Number of Hides (Search 1):</strong> {runningOrderDogClass.num_hides_search_1}</span>
              )}
              {!!runningOrderDogClass?.time_limit_search_1 && (
                <span><strong>Time Limit (Search 1):</strong> {runningOrderDogClass.time_limit_search_1}</span>
              )}
              {/* Search 2 */}
              {!!runningOrderDogClass?.num_hides_search_2 && (
                <span><strong>Number of Hides (Search 2):</strong> {runningOrderDogClass.num_hides_search_2}</span>
              )}
              {!!runningOrderDogClass?.time_limit_search_2 && (
                <span><strong>Time Limit (Search 2):</strong> {runningOrderDogClass.time_limit_search_2}</span>
              )}
              {/* Search 3 (Master Interior only) */}
              {!!runningOrderDogClass?.num_hides_search_3 && (
                <span><strong>Number of Hides (Search 3):</strong> {runningOrderDogClass.num_hides_search_3}</span>
              )}
              {!!runningOrderDogClass?.time_limit_search_3 && (
                <span><strong>Time Limit (Search 3):</strong> {runningOrderDogClass.time_limit_search_3}</span>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.elementNote}>
            <p><strong>Note:</strong> Time limits and number of hides are now configured in the Judge Schedules tab for
              this show.</p>
          </div>
        )}

        <div className={styles.row}>
          <Dropdown value={getResultValue("result")} onChange={(e: any, value: IOption) => {
            updateResult("result", value);
          }} label="Result" options={resultOptions} placeholder=""/>
        </div>

        {requiresNqReason.includes(getResultValue("result").id) && <div className="reason">
          <Dropdown value={results[0].nqReason} label={"NQ Reason"} onChange={(e: any, value: IOption) => {
            updateResult("nqReason", value);
          }} options={nqReasons} placeholder=""/>
        </div>}

        {requiresExcusedReason.includes(getResultValue("result").id) && <>
          <div className="reason">
            <Dropdown value={getResultValue("excusedReason")} label={"Excused Reason"}
                      onChange={(e: any, value: IOption) => {
                        updateResult("excusedReason", value);
                      }} options={excusedReasons} placeholder=""/>
          </div>

          {getResultValue("excusedReason").id === Constants.scentwork_excused_reason.other && <div className={styles.row}>
            <Input label="Other Reason" id="otherReason" type="text" required
                   onChange={(e) => {
                     e.stopPropagation();
                     updateResult("otherReason", e.target.value);
                   }} placeholder="" defaultValue={getResultValue("otherReason")}/>
          </div>}
        </>}

        <div className={styles.row}>
          <StopWatch label="Run Time" time={getResultValue("searchTime")} onChange={(value: string) => {
            updateResult("searchTime", value);
          }}/>
          <span style={{ display: 'block', height: 20 }}/>
          <StopWatch label="Time Limit" time={getResultValue("timeLimit")} onChange={(value: string) => {
            updateResult("timeLimit", value);
          }}/>
        </div>

        <div className={styles.row}>
          <div className={styles.findsInputGroup}>
            <Input
              label="# of Finds"
              id="numFinds"
              type="number"
              min="0"
              max={!!runningOrderDogClass?.num_hides && runningOrderDogClass?.num_hides > 0 ? runningOrderDogClass.num_hides.toString() : undefined}
              required
              defaultValue={getResultValue("numFinds").toString()}
              onChange={(e) => {
                e.stopPropagation();
                updateResult("numFinds", Number(e.target.value));
              }}
              placeholder=""
            />
            <Input
              label="# of Hides"
              id="numHides"
              type="number"
              min={0}
              max={20}
              required
              defaultValue={getResultValue("numHides").toString()}
              onChange={(e) => {
                e.stopPropagation();
                updateResult("numHides", Number(e.target.value));
              }}
              placeholder=""
            />
          </div>
        </div>
        <div className={styles.subTitle}>Faults</div>
        <div className={styles.row}><Input label="Handler Error" id="handlerError" type="number" min="0" required
                                           onChange={(e) => {
                                             e.stopPropagation();
                                             updateResult("handlerError", e.target.value);
                                           }} placeholder="" defaultValue={getResultValue("handlerError")}/></div>
        <div className={styles.row}><Input label="Safety Concern" id="safetyConcern" type="number" min="0" required
                                           onChange={(e) => {
                                             e.stopPropagation();
                                             updateResult("safetyConcern", e.target.value);
                                           }} placeholder="" defaultValue={getResultValue("safetyConcern")}/></div>
        <div className={styles.row}><Input label="Mild Disruption of Area" id="mdoa" type="number" min="0" required
                                           onChange={(e) => {
                                             e.stopPropagation();
                                             updateResult("mdoa", e.target.value);
                                           }} placeholder="" defaultValue={getResultValue("mdoa")}/></div>
      </div>
    )
  }

  // Show tabs for multiple searches (Master all elements, Excellent Interior)
  if (numSearches > 1) {
    return (
      <TabContent setTab={setTab} activeTab={activeTab} tabs={tabs ?? []}>
        {renderResultForm()}
      </TabContent>
    );
  } else {
    return renderResultForm();
  }

};

export default ScentworkResults;